import { useContext, useEffect, useState } from 'react';
import {
  Card,
  Dialog,
  FormControl,
  Grid,
  MenuItem,
  Select,
} from '@mui/material';
import { Trans, useTranslation } from '../hooks/useTranslation';
import moment from 'moment';
import VerticalLogo from '../assets/Iqos vertical logo.svg';
import Under18Dialog from './Under18Dialog';
import PrimaryButton from './PrimaryButton';
import { DataContext } from '../contexts/DataContext';

function daysInMonth(month, year) {
  // February has 29 days in a leap year, 28 otherwise
  if (month + '' === 2 + '') {
    // Check if the year is a leap year
    if ((+year % 4 === 0 && +year % 100 !== 0) || +year % 400 === 0) {
      return 29; // Leap year
    } else {
      return 28; // Not a leap year
    }
  }
  // Months with 31 days
  else if (['4', '6', '9', '11'].includes(month + '')) {
    return 30;
  }
  // All other months have 31 days
  else {
    return 31;
  }
}
export default function AgeConfirmationDialog(props) {
  const { t } = useTranslation();
  const { data, setData } = useContext(DataContext);
  const [day, setDay] = useState('Day');
  const [month, setMonth] = useState('Month');
  const [year, setYear] = useState('Year');
  const [daysCount, setDaysCount] = useState(31);

  const [secondDialog, setSecondDialog] = useState(
    localStorage.getItem('ageConfirmation2') &&
      typeof +localStorage.getItem('ageConfirmation2') === 'number' &&
      !isNaN(+localStorage.getItem('ageConfirmation2')) &&
      Date.now() - +localStorage.getItem('ageConfirmation2') <= 60 * 1000
      ? true
      : false
  );

  const [isOpen, setIsOpen] = useState(
    localStorage.getItem('ageConfirmation') ||
      (localStorage.getItem('ageConfirmation2') &&
        typeof +localStorage.getItem('ageConfirmation2') === 'number' &&
        !isNaN(+localStorage.getItem('ageConfirmation2')) &&
        Date.now() - +localStorage.getItem('ageConfirmation2') <= 60 * 1000)
      ? false
      : true
  );

  const canConfirm = day !== 'Day' && month !== 'Month' && year !== 'Year';
  useEffect(() => {
    setDaysCount(daysInMonth(month, year));
  }, [month, year]);

  useEffect(() => {
  const stored = localStorage.getItem('ageConfirmation');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      const { day, month, year } = parsed;

      if (day && month && year) {
        setData(prev => ({
          ...prev,
          dob: new Date(Number(year), Number(month) - 1, Number(day))
        }));
      }
    } catch (e) {
      console.error('Invalid ageConfirmation data in localStorage', e);
    }
  }
}, []);
  const confirmAgeHandler = () => {
    const birthDate = new Date(year, month - 1, day); // month is 0-based in JS Date
    const today = new Date();

    const age = today.getFullYear() - birthDate.getFullYear();

    // Adjust if the birthday hasn't occurred yet this year
    const isBirthdayPassed =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() &&
        today.getDate() >= birthDate.getDate());

    const userIsAdult = age > 18 || (age === 18 && isBirthdayPassed);
    if (canConfirm && userIsAdult) {
      localStorage.setItem(
        'ageConfirmation',
        JSON.stringify({ day, month, year })
      );
      setData({ ...data, dob: new Date(year, month - 1, day) });
      setIsOpen(false);
      setSecondDialog(false);
      localStorage.removeItem('ageConfirmation2');
    } else {
      setIsOpen(false);
      setSecondDialog(true);
      localStorage.setItem('ageConfirmation2', Date.now());
    }
  };

  if (secondDialog) {
    return (
      <Under18Dialog
        isOpen={secondDialog}
        setIsOpen={() => window.history.back()}
      />
    );
  }

  return (
    <Dialog
      open={isOpen}
      PaperProps={{ style: { borderRadius: 10, margin: '20px' } }}
    >
      <Card
        sx={{
          padding: '15px',
          fontFamily: 'Satoshi-Regular !important',
          color: '#44546A',
          textAlign: 'center',
        }}
      >
        <img src={VerticalLogo} alt="VerticalLogo" />
        <p
          style={{ fontSize: '13px', fontWeight: 500, lineHeight: '18px' }}
        >{`${t('onboarding.ageConfirmationDialog.description')}`}</p>
        <Grid container spacing={1} sx={{ marginTop: '5px' }}>
          <Grid item xs={3.5} sx={{ paddingTop: '21px' }}>
            <FormControl fullWidth>
              <Select value={day} onChange={(e) => setDay(e.target.value)}>
                <MenuItem value="Day">{`${t('onboarding.ageConfirmationDialog.day')}`}</MenuItem>
                {Array.from({ length: daysCount }, (_, i) => i + 1).map(
                  (day) => (
                    <MenuItem key={`d-${day}`} value={String(day)}>
                      {day}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4.5} sx={{ paddingTop: '21px' }}>
            <FormControl fullWidth>
              <Select value={month} onChange={(e) => setMonth(e.target.value)}>
                <MenuItem value="Month">{`${t('onboarding.ageConfirmationDialog.month')}`}</MenuItem>
                {moment.months().map((month, index) => (
                  <MenuItem key={`m-${month}`} value={String(index + 1)}>
                    {`${t(`onboarding.ageConfirmationDialog.months.${index + 1}`)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4} sx={{ paddingTop: '21px' }}>
            <FormControl fullWidth>
              <Select value={year} onChange={(e) => setYear(e.target.value)}>
                <MenuItem value="Year">{`${t('onboarding.ageConfirmationDialog.year')}`}</MenuItem>
                {generateXYears(100).map((year, index) => (
                  <MenuItem key={`ys-${index}`} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <PrimaryButton
          disabled={!canConfirm}
          sx={{ marginTop: '16px', marginBottom: '19px' }}
          onClick={confirmAgeHandler}
        >
          {`${t('onboarding.ageConfirmationDialog.confirmBtn')}`}
        </PrimaryButton>
        <div style={{ fontWeight: 500, fontSize: '10px' }}>
          <Trans
            i18nKey={'onboarding.ageConfirmationDialog.disclaimer'}
            t={t}
            components={{
              br: <br />,
              strong: (
                <span style={{ fontWeight: '700' }}>
                  {t('onboarding.ageConfirmationDialog.disclaimer')}
                </span>
              ),
              policy: (
                <a
                  style={{
                    color: '#44546A',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                  href={t('privacyLink')}
                  rel="noopener noreferrer"
                >
                  {t('onboarding.ageConfirmationDialog.disclaimer')}
                </a>
              ),
            }}
          />
        </div>
      </Card>
    </Dialog>
  );
}

function generateXYears(x) {
  const currentYear = Number(moment().format('YYYY'));
  return Array.from({ length: x }, (_, i) => String(currentYear - i));
}
